import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../../store';
import { fetchDataDefinitions, DataDefinition } from '../../../store/dataDefinitionsSlice';
import { createWorkflow, saveWorkflowCanvas, loadWorkflows } from '../../../store/project/projectSlice';
import { setCurrentWorkflow, resetWorkflow, requestFitView, setWorkflowSettings } from '../../../store/workflow/workflowSlice';
import { DEFAULT_WORKFLOW_SETTINGS } from '../../../types/workflowSettings';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { showConfirm } from '../../common/AppDialog';
import NewDataDefinitionDialog from '../panels/DataDefinition/NewDataDefinitionDialog';
import SqlEditorModal from '../panels/PropertyPanel/SqlEditorModal';
import {
  WizardSpec, FlowKind, OptionKind, buildWorkflow, buildPreview, defaultSpec, validateSpec,
  FLOW_KINDS, flowKindLabel, flowKindDesc, endpointTypes,
} from './buildWorkflow';
import { templatesFor, templateName, templateDesc } from './wizardTemplates';
import { typeLabelOf } from '../nodeCatalog';
import FlowPreview from './FlowPreview';
import { checkDfd } from '../utils/dfdRules';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../../services/capabilities';

interface Props { projectId: string;
  /** 만든 워크플로를 넣을 폴더 경로 (비면 프로젝트 바로 아래) */
  folder?: string; onClose: () => void }

/** 단계 제목·설명은 wizard:step.<key>.title/desc */
const STEPS = [{ key: 'kind' }, { key: 'source' }, { key: 'target' }, { key: 'option' }, { key: 'confirm' }] as const;

/**
 * 워크플로 작성 마법사 — QBase 마법사와 같은 형태(좌측 레일 스테퍼 + 본문 + 하단 진행/버튼).
 * 유형 → 소스 → 타깃 → 옵션 → 확인 순으로 받아 캔버스를 만들어 새 워크플로로 저장한다.
 */
const WorkflowWizard: React.FC<Props> = ({ projectId, folder = '', onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const definitions = useSelector((s: RootState) => s.dataDefinitions.items);
  const connections = useSelector((s: RootState) => s.connections.list);
  const usrid = useSelector((s: RootState) => s.auth.user?.usrid || '');
  const [cursor, setCursor] = useState(0);
  const [spec, setSpec] = useState<WizardSpec>(defaultSpec('db2db'));
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [newDefFor, setNewDefFor] = useState<'source' | 'target' | null>(null);
  const [sqlEditorFor, setSqlEditorFor] = useState<'source' | 'target' | null>(null);
  const [templateId, setTemplateId] = useState<string>('');   // 템플릿은 선택 사항 — 안 골라도 된다
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewBig, setPreviewBig] = useState(false);   // 분할처럼 큰 흐름을 볼 때 넓게

  useEscapeKey(() => { if (!busy) handleClose(); });
  useEffect(() => { dispatch(fetchDataDefinitions()); }, [dispatch]);

  const kinds = endpointTypes(spec.kind);
  const shellOk = useCapabilities()?.shell.allowed !== false;
  const built = useMemo(() => { try { return buildWorkflow(spec, { shell: shellOk }); } catch { return { nodes: [], edges: [], summary: [] }; } }, [spec, shellOk]);
  // 하단 미리보기 — 소스/타깃을 아직 안 골랐어도 '(미지정)' 자리로 채워 유형·옵션에 맞는 그림을 보여 준다
  const preview = useMemo(() => buildPreview(spec, { shell: shellOk }), [spec, shellOk]);
  const templates = templatesFor(spec.kind);
  // 만들어진 캔버스가 DFD 규칙에 맞는지 미리 확인해 보여 준다 (마법사 결과는 항상 '적합' 이어야 한다)
  const dfd = useMemo(() => checkDfd(built.nodes as any, built.edges as any), [built]);
  const patch = (p: Partial<WizardSpec>) => setSpec((s) => ({ ...s, ...p }));
  const patchEnd = (which: 'source' | 'target', p: any) => setSpec((s) => ({ ...s, [which]: { ...s[which], ...p } }));

  const handleClose = async () => {
    if (spec.source.definition || spec.target.definition || spec.name) {
      if (!(await showConfirm(t('wizard:ui.closeConfirm'), { title: t('wizard:ui.closeTitle'), okText: t('common:ui.close') }))) return;
    }
    onClose();
  };

  const goNext = async () => {
    if (cursor < STEPS.length - 1) {
      const stepErrs = stepErrors(cursor);
      setErrors(stepErrs);
      if (stepErrs.length) return;
      setCursor(cursor + 1);
      return;
    }
    // 마지막 단계 — 생성
    const all = validateSpec(spec);
    setErrors(all.map((x) => x.message));
    if (all.length) return;
    setBusy(true);
    try {
      const r = await dispatch(createWorkflow({ projectId, name: spec.name.trim(), description: spec.description || summaryText(), icon: iconFor(spec), folder }));
      if (createWorkflow.rejected.match(r)) throw new Error(r.error?.message || t('wizard:ui.createFailed'));
      const wf = r.payload as any;
      const s = await dispatch(saveWorkflowCanvas({ id: wf.id, nodes: built.nodes, edges: built.edges, settings: DEFAULT_WORKFLOW_SETTINGS, regUsrid: usrid }));
      if (saveWorkflowCanvas.rejected.match(s)) throw new Error(s.error?.message || t('wizard:ui.canvasSaveFailed'));
      // 만든 워크플로를 캔버스에 연다
      dispatch(resetWorkflow());
      dispatch(setCurrentWorkflow({ id: wf.id, name: spec.name.trim() }));
      dispatch(setWorkflowSettings(DEFAULT_WORKFLOW_SETTINGS));
      built.nodes.forEach((n) => dispatch({ type: 'workflow/addNode', payload: n }));
      built.edges.forEach((e) => dispatch({ type: 'workflow/addEdge', payload: e }));
      dispatch(requestFitView());
      dispatch(loadWorkflows(projectId));
      onClose();
    } catch (e) {
      setErrors([(e as Error).message]);
    } finally {
      setBusy(false);
    }
  };

  const stepErrors = (idx: number): string[] => {
    const key = STEPS[idx].key;
    if (key === 'kind' || key === 'confirm') return [];
    return validateSpec(spec).filter((x) => x.step === key).map((x) => x.message);
  };
  const summaryText = () => built.summary.join(' / ');
  const iconFor = (s: WizardSpec) => (s.option === 'partition' ? '🧩' : s.option === 'verify' ? '🛡️' : s.kind === 'db2db' ? '🔄' : s.kind === 'file2db' ? '📄' : s.kind === 'db2file' ? '🗂' : '📑');

  const defsFor = (ty: 'db' | 'sam') => definitions.filter((d) => d.type === ty);
  const inp = 'border border-gray-300 rounded px-2 py-1 text-sm bg-white';

  /* ── 각 단계 본문 ─────────────────────────────────────────── */
  const EndpointStep = ({ which }: { which: 'source' | 'target' }) => {
    const end = spec[which];
    const dtype = which === 'source' ? kinds.source : kinds.target;
    const canSql = dtype === 'db';   // SAM 파일은 SQL 로 대체할 수 없다
    return (
      <div>
        {canSql && (
          <div className="flex items-center gap-4 mb-3 text-sm">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={end.mode === 'definition'} onChange={() => patchEnd(which, { mode: 'definition' })} />
              {t('wizard:ui.useDefinition')}
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={end.mode === 'sql'} onChange={() => patchEnd(which, { mode: 'sql' })} />
              {t('wizard:ui.useSql')}
            </label>
          </div>
        )}

        {end.mode === 'definition' ? (
          <>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-600">{t('wizard:ui.definitionOf', { kind: typeLabelOf(dtype === 'db' ? 'rdbData' : 'csvData') })}</span>
              <button type="button" className="px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                onClick={() => setNewDefFor(which)}>{t('wizard:ui.newDefinition')}</button>
            </div>
            <div className="border border-gray-200 rounded max-h-56 overflow-auto">
              {defsFor(dtype).length === 0 ? (
                <div className="p-3 text-xs text-gray-500">{t('wizard:ui.noDefinitions')}</div>
              ) : defsFor(dtype).map((d) => (
                <button key={d.id} type="button"
                  className={`w-full text-left px-2 py-1.5 text-sm flex items-center gap-2 ${end.definition?.id === d.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}
                  onClick={() => patchEnd(which, { definition: d })}>
                  <span>{d.type === 'sam' ? '📄' : '🗄'}</span>
                  <span className="flex-1 truncate">{d.name}</span>
                  <span className="text-[0.6875rem] text-gray-400 truncate max-w-[45%]">
                    {d.type === 'sam' ? d.source.filepath : `${d.source.connection} / ${d.source.schema ? `${d.source.schema}.` : ''}${d.source.table}`}{t('wizard:ui.columns', { n: d.columns.length })}
                  </span>
                </button>
              ))}
            </div>
            {end.definition && (
              <div className="mt-2 text-[0.6875rem] text-gray-500">
                {t('wizard:ui.selected')}<b className="text-gray-700">{end.definition.name}</b>{t('wizard:ui.fieldCount', { n: end.definition.columns.length })}
                {end.definition.columns.length > 0 && <> ({end.definition.columns.slice(0, 6).map((c) => c.name).join(', ')}{end.definition.columns.length > 6 ? ' …' : ''})</>}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-600 w-16">{t('wizard:ui.connection')}</span>
              <select className={inp} value={end.connection || ''} onChange={(e) => patchEnd(which, { connection: e.target.value })}>
                <option value="">{t('wizard:ui.select')}</option>
                {connections.map((c: any) => <option key={c.connection_id} value={c.connection_id}>{c.connection_id}{c.conn_type ? ` (${c.conn_type})` : ''}</option>)}
              </select>
              <button type="button" className="ml-auto px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                onClick={() => setSqlEditorFor(which)}>{t('wizard:ui.sqlEditor')}</button>
            </div>
            <textarea className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono bg-white" rows={8}
              value={end.sql || ''} onChange={(e) => patchEnd(which, { sql: e.target.value })}
              placeholder={which === 'source'
                ? 'SELECT order_id, customer, amount FROM of_test_src_orders WHERE status = \'A\';'
                : 'INSERT INTO of_test_tgt_orders (order_id, customer, amount)\nSELECT order_id, customer, amount FROM stg_orders;'} />
            <div className="text-[0.6875rem] text-gray-400 mt-1">
              {which === 'source' ? t('wizard:ui.srcSqlHint') : t('wizard:ui.tgtSqlHint')}
            </div>
          </>
        )}
      </div>
    );
  };

  const OptionCard = ({ v, title, desc }: { v: OptionKind; title: string; desc: string }) => (
    <button type="button" onClick={() => patch({ option: v })}
      className={`w-full text-left border rounded p-2.5 mb-2 ${spec.option === v ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
      <div className={`text-sm font-medium ${spec.option === v ? 'text-blue-700' : 'text-gray-800'}`}>{title}</div>
      <div className="text-[0.6875rem] text-gray-500 mt-0.5 whitespace-pre-line">{desc}</div>
    </button>
  );

  const body = () => {
    switch (STEPS[cursor].key) {
      case 'kind':
        return (
          <div>
            <div className="grid grid-cols-2 gap-2">
              {FLOW_KINDS.map((k: FlowKind) => (
                <button key={k} type="button"
                  onClick={() => { setSpec({ ...defaultSpec(k), name: spec.name, description: spec.description }); setTemplateId(''); }}
                  className={`text-left border rounded p-3 ${spec.kind === k ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <div className={`text-sm font-semibold ${spec.kind === k ? 'text-blue-700' : 'text-gray-800'}`}>{flowKindLabel(k)}</div>
                  <div className="text-[0.6875rem] text-gray-500 mt-1">{flowKindDesc(k)}</div>
                </button>
              ))}
            </div>

            {/* 유형별 템플릿 — 고르면 옵션·적재 방식이 한 번에 채워진다. 선택하지 않아도 된다. */}
            <div className="mt-4">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs font-semibold text-gray-600">{t('wizard:ui.templates', { kind: flowKindLabel(spec.kind) })}</span>
                <span className="text-[0.6875rem] text-gray-400">{t('wizard:ui.templatesOptional')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {templates.map((tp) => (
                  <button key={tp.id} type="button"
                    onClick={() => {
                      if (templateId === tp.id) { setTemplateId(''); setSpec({ ...defaultSpec(spec.kind), name: spec.name, description: spec.description }); return; }
                      setTemplateId(tp.id);
                      setSpec((s) => tp.apply(s));
                    }}
                    className={`text-left border rounded p-2.5 ${templateId === tp.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <div className={`text-xs font-semibold flex items-center gap-1.5 ${templateId === tp.id ? 'text-blue-700' : 'text-gray-800'}`}>
                      <span>{tp.icon}</span><span className="truncate">{templateName(tp)}</span>
                      {templateId === tp.id && <span className="ml-auto shrink-0 text-[0.625rem] font-normal text-blue-600">{t('wizard:ui.templateSelected')}</span>}
                    </div>
                    <div className="text-[0.6875rem] text-gray-500 mt-1">{templateDesc(tp)}</div>
                    {templateId === tp.id && <div className="text-[0.625rem] text-blue-500 mt-1">{t('wizard:ui.templateDeselect')}</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      case 'source': return <EndpointStep which="source" />;
      case 'target':
        return (
          <div>
            <EndpointStep which="target" />
            {spec.target.mode === 'definition' && kinds.target === 'db' && (
              <div className="mt-3 rounded border border-gray-200 p-2">
                <div className="text-xs font-semibold text-gray-600 mb-1">{t('wizard:ui.loadMode')}</div>
                <div className="flex items-center gap-4 text-xs text-gray-700 mb-1.5">
                  {(['WRITE', 'APPEND', 'UPSERT'] as const).map((m) => (
                    <label key={m} className="flex items-center gap-1 cursor-pointer">
                      <input type="radio" checked={spec.load.mode === m} onChange={() => patch({ load: { ...spec.load, mode: m } })} />
                      {m === 'WRITE' ? t('wizard:ui.modeWrite') : m === 'APPEND' ? t('wizard:ui.modeAppend') : t('wizard:ui.modeUpsert')}
                    </label>
                  ))}
                </div>
                {spec.load.mode === 'UPSERT' && (
                  <div className="flex flex-wrap items-center gap-1 mb-1">
                    {spec.load.keyColumns.map((k, i) => (
                      <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-xs font-mono text-gray-700">{k}
                        <button className="text-gray-400 hover:text-red-500" onClick={() => patch({ load: { ...spec.load, keyColumns: spec.load.keyColumns.filter((_, j) => j !== i) } })}>✕</button>
                      </span>
                    ))}
                    <select className={inp} value="" onChange={(e) => { if (e.target.value) patch({ load: { ...spec.load, keyColumns: [...spec.load.keyColumns, e.target.value] } }); }}>
                      <option value="">{t('wizard:ui.addKey')}</option>
                      {(spec.target.definition?.columns || []).map((c) => c.name).filter((c) => !spec.load.keyColumns.includes(c)).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                <label className="flex items-center gap-1.5 text-xs text-gray-700">
                  <input type="checkbox" checked={spec.load.useBulkLoader} onChange={(e) => patch({ load: { ...spec.load, useBulkLoader: e.target.checked } })} />
                  {t('wizard:ui.bulkLoader')}
                </label>
              </div>
            )}
          </div>
        );
      case 'option':
        return (
          <div>
            <OptionCard v="basic" title={t('wizard:opt.basic')} desc={t('wizard:opt.basicDesc')} />
            <OptionCard v="verify" title={t('wizard:opt.verify')} desc={t('wizard:opt.verifyDesc')} />
            <OptionCard v="partition" title={t('wizard:opt.partition')} desc={t('wizard:opt.partitionDesc')} />
            {spec.option === 'partition' && (
              <div className="rounded border border-gray-200 p-2.5 mt-1">
                <div className="flex flex-col gap-1.5 text-xs text-gray-700 mb-2">
                  <label className="flex items-start gap-1.5 cursor-pointer">
                    <input type="radio" className="mt-0.5" checked={spec.partition.mode === 'range'} onChange={() => patch({ partition: { ...spec.partition, mode: 'range' } })} />
                    <span><b>{t('wizard:opt.range')}</b>{t('wizard:opt.rangeDesc')}</span>
                  </label>
                  <label className="flex items-start gap-1.5 cursor-pointer">
                    <input type="radio" className="mt-0.5" checked={spec.partition.mode === 'dblink'} onChange={() => patch({ partition: { ...spec.partition, mode: 'dblink' } })} />
                    <span><b>{t('wizard:opt.dblink')}</b>{t('wizard:opt.dblinkDesc')}</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-1.5 text-xs text-gray-700">{t('wizard:opt.count')}
                    <input type="number" min={2} max={32} className={`${inp} w-20`} value={spec.partition.count}
                      onChange={(e) => patch({ partition: { ...spec.partition, count: Number(e.target.value) || 8 } })} />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-700">{t('wizard:opt.keyColumn')}
                    {spec.source.definition?.columns.length ? (
                      <select className={inp} value={spec.partition.keyColumn} onChange={(e) => patch({ partition: { ...spec.partition, keyColumn: e.target.value } })}>
                        <option value="">{t('wizard:ui.select')}</option>
                        {spec.source.definition.columns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                      </select>
                    ) : (
                      <input className={`${inp} font-mono w-32`} value={spec.partition.keyColumn} placeholder={t('wizard:opt.keyColumnPh')}
                        onChange={(e) => patch({ partition: { ...spec.partition, keyColumn: e.target.value } })} />
                    )}
                  </label>
                  {spec.partition.mode === 'dblink' && (
                    <>
                      <label className="flex items-center gap-1.5 text-xs text-gray-700">{t('wizard:opt.dbLinkName')}
                        <input className={`${inp} font-mono w-32`} value={spec.partition.dbLinkName || ''} placeholder="TGT_LINK"
                          onChange={(e) => patch({ partition: { ...spec.partition, dbLinkName: e.target.value } })} />
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-gray-700">{t('wizard:opt.tempPrefix')}
                        <input className={`${inp} font-mono w-28`} value={spec.partition.tempPrefix || ''} placeholder="of_tmp"
                          onChange={(e) => patch({ partition: { ...spec.partition, tempPrefix: e.target.value } })} />
                      </label>
                    </>
                  )}
                </div>
                <div className="text-[0.6875rem] text-gray-400 mt-1.5">
                  {t('wizard:opt.modHint')}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return (
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('wizard:confirm.name')}</label>
            <input className={`${inp} w-full mb-3`} value={spec.name} autoFocus
              placeholder={`${flowKindLabel(spec.kind)} — ${spec.source.definition?.name || 'SQL'} → ${spec.target.definition?.name || 'SQL'}`}
              onChange={(e) => patch({ name: e.target.value })} />
            <div className="rounded border border-gray-200 p-2.5 mb-2">
              <div className="text-xs font-semibold text-gray-600 mb-1">{t('wizard:confirm.flow')}</div>
              <ul className="text-xs text-gray-700 list-disc pl-4 space-y-0.5">
                {built.summary.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
              <div className="text-[0.6875rem] text-gray-500 mt-1.5">
                {t('wizard:confirm.counts', { nodes: built.nodes.length, edges: built.edges.length })}
                {dfd.length === 0
                  ? <span className="text-green-600">{t('wizard:confirm.dfdOk')}</span>
                  : <span className="text-amber-600" title={dfd.map((v) => v.message).join(' / ')}>{t('wizard:confirm.dfdBad', { n: dfd.length })}</span>}
              </div>
            </div>
            <div className="text-[0.6875rem] text-gray-500">
              {t('wizard:confirm.template', { name: (() => { const tp = templates.find((x) => x.id === templateId); return tp ? templateName(tp) : t('wizard:confirm.noTemplate'); })() })}
            </div>
          </div>
        );
    }
  };

  const done = (i: number) => i < cursor;
  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={() => { if (!busy) handleClose(); }}>
      <div className="bg-white rounded-lg shadow-2xl w-[960px] max-w-[95vw] h-[88vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('wizard:ui.title')} <span className="ml-2 text-xs font-normal text-gray-500">{flowKindLabel(spec.kind)}</span></div>
            <div className="text-[0.6875rem] text-gray-500">{t('wizard:ui.subtitle')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={handleClose} title={t('datadef:common.closeEsc')}>✕</button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-[220px_1fr] divide-x divide-gray-200">
          <div className="overflow-auto py-2 bg-gray-50">
            {STEPS.map((s, i) => (
              <button key={s.key} type="button" onClick={() => !busy && setCursor(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-l-2 ${i === cursor ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-transparent text-gray-700 hover:bg-gray-100'}`}>
                <span className={`w-4 text-center text-xs ${done(i) ? 'text-green-600' : 'text-gray-400'}`}>{done(i) ? '✓' : '○'}</span>
                {t(`wizard:step.${s.key}.title`)}
              </button>
            ))}
          </div>
          <div className="overflow-auto p-4 min-h-0">
            <div className="text-sm font-semibold text-gray-800">{t(`wizard:step.${STEPS[cursor].key}.title`)}</div>
            <div className="text-[0.6875rem] text-gray-500 mb-3">{t(`wizard:step.${STEPS[cursor].key}.desc`)}</div>
            {body()}
            {errors.length > 0 && (
              <div className="mt-3 rounded border border-red-300 bg-red-50 text-red-700 text-xs px-2.5 py-2">
                {errors.join(' · ')}
              </div>
            )}
          </div>
        </div>

        {/* 하단 워크플로 미리보기 — 유형·옵션을 바꾸면 그림이 그대로 따라 바뀐다 */}
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2 px-4 py-1.5">
            <button type="button" className="text-xs font-semibold text-gray-600 flex items-center gap-1 hover:text-gray-800"
              onClick={() => setPreviewOpen(!previewOpen)}>
              <span className={`tree-chevron ${previewOpen ? 'open' : ''}`}>›</span> {t('wizard:ui.preview')}
            </button>
            <span className="text-[0.6875rem] text-gray-500">
              {t('wizard:ui.previewCounts', { nodes: preview.nodes.length, edges: preview.edges.length })}
              {(!spec.source.definition && spec.source.mode === 'definition') || (!spec.target.definition && spec.target.mode === 'definition')
                ? t('wizard:ui.previewFill') : ''}
            </span>
            <button type="button" className="ml-auto text-[0.6875rem] px-1.5 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
              onClick={() => { setPreviewOpen(true); setPreviewBig(!previewBig); }}>
              {previewBig ? t('wizard:ui.smaller') : t('wizard:ui.bigger')}
            </button>
            <span className="flex items-center gap-2 text-[0.625rem] text-gray-500">
              <span className="inline-flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#dbeafe' }} />{t('wizard:legend.data')}</span>
              <span className="inline-flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#dcfce7' }} />{t('wizard:legend.process')}</span>
              <span className="inline-flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#f3e8ff' }} />{t('wizard:legend.control')}</span>
              <span className="inline-flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#fef3c7' }} />{t('wizard:legend.exec')}</span>
            </span>
          </div>
          {previewOpen && <FlowPreview nodes={preview.nodes} edges={preview.edges} height={previewBig ? 340 : 150} className="px-4 pb-2" />}
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center gap-2">
          <span className="text-[0.6875rem] text-gray-500 mr-auto"><b>{cursor + 1}</b>{t('wizard:ui.stepOf', { total: STEPS.length, n: built.nodes.length })}</span>
          <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 disabled:opacity-50"
            disabled={cursor === 0 || busy} onClick={() => setCursor(cursor - 1)}>{t('wizard:ui.prev')}</button>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            disabled={busy} onClick={goNext}>{busy ? t('wizard:ui.creating') : cursor === STEPS.length - 1 ? t('wizard:ui.create') : t('wizard:ui.next')}</button>
        </div>
      </div>

      {newDefFor && (
        <NewDataDefinitionDialog
          type={newDefFor === 'source' ? kinds.source : kinds.target}
          folderPath={(newDefFor === 'source' ? kinds.source : kinds.target) === 'sam' ? 'sam' : 'db'}
          onCreated={(item: DataDefinition) => patchEnd(newDefFor, { definition: item, mode: 'definition' })}
          onClose={() => setNewDefFor(null)}
        />
      )}
      {sqlEditorFor && (
        <SqlEditorModal
          title={t('wizard:ui.sqlEditTitle', { what: sqlEditorFor === 'source' ? t('wizard:ui.sourceQuery') : t('wizard:ui.targetProc') })}
          value={spec[sqlEditorFor].sql || ''}
          connection={spec[sqlEditorFor].connection}
          onSave={(v) => { patchEnd(sqlEditorFor, { sql: v }); setSqlEditorFor(null); }}
          onClose={() => setSqlEditorFor(null)}
        />
      )}
    </div>
  );
};

export default WorkflowWizard;
